import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";

import { fetchMyProjectTasks, patchMyProjectTask } from "../lib/api";
import { adaptMyProjectTask } from "../lib/projectTaskAdapter";
import type { DueTask, MyProjectTaskOut } from "../types";

// Sprint-017 E7 (ADR-003 §4/§7, design §7) — the DE-IDENTIFIED assignee
// feed hook. Fetches `GET /me/project-tasks`, adapts each item to the
// EXISTING `DueTask` shape (via `adaptMyProjectTask` — the P0 boundary),
// and exposes a `done`-toggle that writes back through
// `PATCH /me/project-tasks/{id}` — never through the /due engine, never
// exposing a project id.
//
// **Unread signal (design §7.4 / Flow 6):** a project-agnostic dot on the
// My list tab, cleared on visit. `unreadCount` counts not-done assigned
// tasks the person hasn't "seen" yet; `markSeen()` (called when My list is
// opened) clears it. The seen set is persisted so a new task keeps its dot
// across reloads until the list is actually visited. The dot NEVER appears
// on any Projects entry (that would leak project origin) — only My list.

const SEEN_STORAGE_KEY = "hayloft.myProjectTasks.seen";

function loadSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? new Set(parsed.filter((x): x is string => typeof x === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

function persistSeen(seen: Set<string>): void {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...seen]));
  } catch {
    // localStorage unavailable (private mode / disabled) — the dot simply
    // won't persist across reloads; a non-fatal degrade, never a crash.
  }
}

export interface UseMyProjectTasksResult {
  /** Adapted, de-identified rows that are NOT done — merged inline into the
   * caller's "Whenever you get a chance" bucket. */
  openTasks: DueTask[];
  /** Adapted, de-identified rows that ARE done — merged inline into
   * "Done today". */
  doneTasks: DueTask[];
  /** The set of task ids this feed owns — the caller uses it to route a
   * `done`-toggle back through `toggle` instead of the /due path. */
  ids: Set<string>;
  /** Toggle `done` on one of my assigned tasks (optimistic). */
  toggle: (taskId: string, done: boolean) => Promise<void>;
  /** Number of not-done tasks not yet seen — drives the My list tab dot. */
  unreadCount: number;
  /** Clear the unread signal (call when My list is opened). */
  markSeen: () => void;
  refresh: () => Promise<void>;
}

export function useMyProjectTasks(meId: string, enabled = true): UseMyProjectTasksResult {
  const [items, setItems] = useState<MyProjectTaskOut[]>([]);
  const [seen, setSeen] = useState<Set<string>>(() => loadSeen());
  const seenRef = useRef(seen);
  seenRef.current = seen;

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const rows = await fetchMyProjectTasks();
      setItems(rows);
    } catch {
      // A feed fetch failure must never blank the user's existing list —
      // leave the merged Today/My list unchanged and try again next mount.
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggle = useCallback(async (taskId: string, done: boolean) => {
    // Optimistic: reflect the new done state immediately, mirroring the
    // chore-toggle behavior (no snackbar for a check).
    setItems((prev) =>
      prev.map((t) =>
        t.id === taskId ? { ...t, done, completed_at: done ? new Date().toISOString() : null } : t,
      ),
    );
    try {
      const updated = await patchMyProjectTask(taskId, done, crypto.randomUUID());
      setItems((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch {
      // Revert on failure (same posture as the /due optimistic path).
      setItems((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, done: !done, completed_at: !done ? new Date().toISOString() : null }
            : t,
        ),
      );
    }
  }, []);

  const openTasks = useMemo(
    () => items.filter((i) => !i.done).map((i) => adaptMyProjectTask(i, meId)),
    [items, meId],
  );
  const doneTasks = useMemo(
    () => items.filter((i) => i.done).map((i) => adaptMyProjectTask(i, meId)),
    [items, meId],
  );
  const ids = useMemo(() => new Set(items.map((i) => i.id)), [items]);

  const unreadCount = useMemo(
    () => items.filter((i) => !i.done && !seen.has(i.id)).length,
    [items, seen],
  );

  const markSeen = useCallback(() => {
    const openIds = items.filter((i) => !i.done).map((i) => i.id);
    if (openIds.length === 0) return;
    const next = new Set(seenRef.current);
    let changed = false;
    for (const id of openIds) {
      if (!next.has(id)) {
        next.add(id);
        changed = true;
      }
    }
    if (changed) {
      setSeen(next);
      persistSeen(next);
    }
  }, [items]);

  return { openTasks, doneTasks, ids, toggle, unreadCount, markSeen, refresh };
}
